import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  RefreshButton,
} from "../ui";
import ReportsDateFilter from "./ReportsDateFilter";
import ReportsSubNav from "./ReportsSubNav";
import { DowntimePayload } from "./reportsTypes";

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

export default function DowntimeReport() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState<DowntimePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<DowntimePayload>("/reports/downtime", {
        params: { from: f, to: t },
      });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load downtime report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const worstLocationId = data && data.rows.length ? data.rows[0].locationId : null;

  return (
    <PageContainer>
      <PageHeader
        title="Downtime by store"
        subtitle={
          data
            ? `${data.range.from} to ${data.range.to} · time spent Out of Order, Network Error or Unavailable`
            : undefined
        }
        actions={<RefreshButton onClick={() => load(from, to)} loading={loading} />}
      />

      <div className="sq-stack">
        <ReportsSubNav />
        <ReportsDateFilter from={from} to={to} onApply={applyRange} loading={loading} />

        {error ? <ErrorBanner onRetry={() => load(from, to)}>{error}</ErrorBanner> : null}
        {loading && !data ? (
          <Card>
            <LoadingPanel label="Loading downtime" />
          </Card>
        ) : null}

        {data ? (
          <Card
            flush
            title="Ranked by total downtime"
            subtitle="Worst store first. Includes any machine currently down (counted to now)."
            aria-label="Downtime by store"
          >
            {data.rows.length === 0 ? (
              <EmptyState title="No downtime recorded in this range" />
            ) : (
              <div className="sq-table-wrap">
                <table className="sq-table is-stacked cols-3">
                  <thead>
                    <tr>
                      <th scope="col">Store</th>
                      <th scope="col" className="num">Downtime hours</th>
                      <th scope="col" className="num">Incidents</th>
                      <th scope="col" className="num">Avg per incident</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr
                        key={row.locationId}
                        className={row.locationId === worstLocationId ? "is-flagged" : undefined}
                      >
                        <td className="cell-title">
                          <div className="sq-strong">{row.name}</div>
                          {row.locationPin ? (
                            <div className="sq-muted">{row.locationPin}</div>
                          ) : null}
                        </td>
                        <td className="num sq-strong" data-label="Downtime">
                          {row.downtimeHours}h
                        </td>
                        <td className="num" data-label="Incidents">
                          {row.incidentCount}
                        </td>
                        <td className="num" data-label="Avg">
                          {row.incidentCount > 0
                            ? `${Math.round((row.downtimeHours / row.incidentCount) * 10) / 10}h`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        ) : null}
      </div>
    </PageContainer>
  );
}
